import clsx from 'clsx';
import { useAuth } from 'context/auth';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import { Icon } from 'common/icon';
import { Menu } from 'components/menu';
import { LoginModal } from 'components/modal/login';

import SearchIcon from 'assets/icons/search-main.svg';

import { ResultSearch } from './result-search';

import { Routes } from 'constants/routes';
import { useLoginModal } from 'hooks';
import Link from 'next/link';
import { useMeQuery } from 'hooks/api/users';

import { Analytics } from 'constants/analytics';
import { syncShallowSearchUrl } from 'utils/search-query';
import styles from './styles.module.css';
import { MenuSupport } from './menu-support';
import { Avatar } from 'components/template/publication/comment';

const Logo = ({ isMain = false }: { isMain?: boolean }) => {
  return (
    <Link href={Routes.home} className={clsx(isMain && styles.default_cursor)}>
      <div className={styles.footerLogo}>
        <Icon.Logo />
      </div>
    </Link>
  );
};

export const MenuFooter = ({
  isSearchInputVisible,
}: {
  isSearchInputVisible?: boolean;
}) => {
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);
  const search = router.query.q
    ? String(decodeURIComponent(router.query.q?.toString()))
    : '';

  const searchRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isLoggedIn } = useAuth();
  const { data: me } = useMeQuery({ isSafe: true }, { skip: !isMounted });
  const { user } = me ?? {};
  const showLoggedIn = isMounted && isLoggedIn;
  const [filter, setFilter] = useState('');
  const debounceValue = useDebounce(filter, 500);
  const [isOpen, setOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initialize with current scroll position
    setScrollY(window.scrollY);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (search) {
      setFilter(search);
      searchRef.current?.focus();
    }
  }, [search]);

  useEffect(() => {
    syncShallowSearchUrl(router, Routes.home, debounceValue, 'q');
  }, [debounceValue, router]);

  const { handleCloseLoginModal, handleConfirmLoginModal, isLoginVisible } =
    useLoginModal();

  const handleProfile = useCallback(() => {
    if (showLoggedIn) {
      router.push('/profile');
    } else {
      router.push('/login');
    }
  }, [showLoggedIn, router]);

  return (
    <div suppressHydrationWarning={true}>
      <footer className={styles.footerMobile}>
        <div>
          <div
            className={clsx(styles.container, isOpen && styles.container_no_bg)}
          >
            {(isSearchInputVisible || scrollY > 300) && (
              <div
                className={clsx(styles.footerSearch, styles.search)}
                ref={containerRef}
              >
                <ResultSearch
                  searchPosition="relative"
                  searchRef={searchRef}
                  searchText={debounceValue}
                  containerRef={containerRef}
                />
                <div className={styles.wrapperButton}>
                  <button className={styles.searchButton}>
                    <Image src={SearchIcon} alt="search-icon" />
                  </button>
                  <input
                    type="text"
                    value={filter}
                    ref={searchRef}
                    className={styles.searchInput}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder={'Check out the RTP online casino...'}
                    autoComplete="off"
                  />
                  {(filter.length || search?.length) && (
                    <button
                      onClick={() => {
                        setFilter('');
                        searchRef.current?.focus();
                      }}
                      className={styles.clearContainer}
                    >
                      <Icon.Close height={20} width={20} />
                      <span>{'Clear'}</span>
                    </button>
                  )}
                </div>
              </div>
            )}
            <div id={Analytics.general.headerMenu} className={styles.footerRow}>
              <Logo isMain />
              <div className={styles.menu}>
                <MenuSupport />
                <div
                  className={styles.menu_btn}
                  id={Analytics.general.headerLogin}
                  onClick={handleProfile}
                >
                  <div
                    className={clsx(styles.footerLoginLink, {
                      [styles.footerUnLogged]: !showLoggedIn,
                    })}
                    suppressHydrationWarning
                  >
                    {showLoggedIn ? (
                      <Avatar
                        src={user?.imageUrl || ''}
                        alt={'user sidebarmenu'}
                        width={25}
                        height={25}
                      />
                    ) : (
                      <Icon.User width="25px" height="25px" />
                    )}
                  </div>
                </div>

                <Menu onOpenChange={setOpen} />

                <LoginModal
                  isVisible={isLoginVisible}
                  onClose={handleCloseLoginModal}
                  confirmOnClick={handleConfirmLoginModal}
                />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
